/**
 * One checklist line on the capture page (PLAN §6 steps 5–6).
 *
 * A line not open on the car is graded OK / Fix soon / Must fix. A line an earlier visit left open
 * is pinned and re-graded with the §3.4 options: Fixed, No fix needed, then Still must fix and Down
 * to fix soon (an open Must fix) or Still fix soon and Now must fix (an open Fix soon); OK is never
 * offered, because the record must say how an open line closed.
 *
 * A line that is not OK opens in place: a required note with chips, a camera that tags the photo to
 * this line, and — on a new Must fix — "Fixed during inspection", which sends the line as Fixed and
 * lifts the photo requirement. Every control is a tap target; nothing is hover-only.
 */

import { useId, useState } from 'react';
import { Info, Loader2, RotateCw, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { NOTE_CHIPS, carriedLabel, formatShortDay, type Grade, type OpenItem } from '@/lib/inspections';
import { chooseGrade, type DraftLine } from '@/lib/inspectionDraft';
import { GradeBadge } from '../InspectionChip';
import { PhotoThumb } from '../PhotoZoom';
import PhotoInputs from './PhotoInputs';
import type { PhotoUpload } from './usePhotoUploads';

type Option = { grade: Grade; label: string };

const NEW_OPTIONS: Option[] = [
    { grade: 'ok', label: 'OK' },
    { grade: 'fix_soon', label: 'Fix soon' },
    { grade: 'fix_now', label: 'Must fix' },
];

const OPEN_MUST_FIX: Option[] = [
    { grade: 'fixed', label: 'Fixed' },
    { grade: 'not_needed', label: 'No fix needed' },
    { grade: 'fix_now', label: 'Still must fix' },
    { grade: 'fix_soon', label: 'Down to fix soon' },
];

const OPEN_FIX_SOON: Option[] = [
    { grade: 'fixed', label: 'Fixed' },
    { grade: 'not_needed', label: 'No fix needed' },
    { grade: 'fix_soon', label: 'Still fix soon' },
    { grade: 'fix_now', label: 'Now must fix' },
];

const SELECTED: Record<Grade, string> = {
    ok: 'border-emerald-600 bg-emerald-600 text-white',
    fixed: 'border-emerald-600 bg-emerald-600 text-white',
    fix_soon: 'border-amber-500 bg-amber-500 text-white',
    fix_now: 'border-red-600 bg-red-600 text-white',
    not_needed: 'border-slate-600 bg-slate-600 text-white',
};

export type RowProblem = 'unanswered' | 'note' | 'photo';

export interface CheckRowProps {
    item: string;
    label: string;
    guidance?: string | null;
    /** A fact shown under the label, e.g. the km since the last service beside Servicing. */
    hint?: string | null;
    /** Set for a pinned line: the car's open item it re-grades. */
    open?: OpenItem | null;
    line: DraftLine;
    photoRequired: boolean;
    problems: ReadonlySet<RowProblem>;
    /** Show unanswered and missing-note problems in red (after a look at the preview or a submit). */
    showProblems: boolean;
    /** This line's uploads not yet confirmed into the draft. */
    pending: PhotoUpload[];
    /** Local previews by stored URL, for photos uploaded in this session. */
    previews: Record<string, string>;
    onChange: (patch: Partial<DraftLine>) => void;
    onPhoto: (file: File) => void;
    onRemovePhoto: (url: string) => void;
    onRetry: (name: string) => void;
    onDismiss: (name: string) => void;
    /** A line added to a re-check can be taken off again. */
    onRemoveLine?: () => void;
}

export default function CheckRow(props: CheckRowProps) {
    const { item, label, guidance, hint, open, line, photoRequired, problems, showProblems, pending, previews } = props;
    const [showGuide, setShowGuide] = useState(false);
    const noteId = useId();
    const options = open ? (open.grade === 'fix_now' ? OPEN_MUST_FIX : OPEN_FIX_SOON) : NEW_OPTIONS;
    const flagged = !!line.grade && line.grade !== 'ok';
    const noteNeeded = line.grade === 'fix_soon' || line.grade === 'fix_now' || line.grade === 'not_needed';
    const unanswered = showProblems && problems.has('unanswered');
    const carried = open ? carriedLabel(open.times_carried) : null;
    const photoCount = line.photos.length + pending.filter((p) => p.status !== 'done').length;

    const choose = (grade: Grade) => {
        if (grade === line.grade) return;
        props.onChange(chooseGrade(line, grade, open));
    };

    const addChip = (word: string) => {
        const note = line.note.trim();
        props.onChange({ note: note ? `${note}, ${word}` : word.charAt(0).toUpperCase() + word.slice(1) });
    };

    return (
        <div
            id={`line-${item}`}
            className={cn(
                'scroll-mt-40 rounded-xl border bg-card p-3 sm:p-4',
                unanswered && 'border-red-500 ring-1 ring-red-500',
                open && 'border-l-4',
                open?.grade === 'fix_now' && 'border-l-red-500',
                open?.grade === 'fix_soon' && 'border-l-amber-500',
            )}
        >
            <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                    <p className="font-medium leading-snug">{label}</p>
                    {hint && <p className="mt-0.5 text-sm text-muted-foreground">{hint}</p>}
                    {open && (
                        <p className="mt-0.5 text-sm text-muted-foreground">
                            <GradeBadge grade={open.grade} className="mr-1.5 align-middle" />
                            Since {formatShortDay(open.raised_on)}
                            {carried && ` (${carried})`}
                            {open.note && `: ${open.note}`}
                            {open.raised_before_contract && <span className="ml-1.5 text-xs">· raised before this contract</span>}
                        </p>
                    )}
                </div>
                <div className="flex shrink-0 items-center">
                    {guidance && (
                        <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-9 w-9 text-muted-foreground"
                            onClick={() => setShowGuide((v) => !v)}
                            aria-expanded={showGuide}
                            aria-label={`Guidance for ${label}`}
                        >
                            <Info />
                        </Button>
                    )}
                    {props.onRemoveLine && (
                        <Button type="button" variant="ghost" size="icon" className="h-9 w-9 text-muted-foreground" onClick={props.onRemoveLine} aria-label={`Take ${label} off this re-check`}>
                            <X />
                        </Button>
                    )}
                </div>
            </div>
            {showGuide && guidance && <p className="mt-2 rounded-md bg-muted px-3 py-2 text-sm text-muted-foreground">{guidance}</p>}

            <div role="radiogroup" aria-label={label} className={cn('mt-3 grid gap-2', options.length === 4 ? 'grid-cols-2 sm:grid-cols-4' : 'grid-cols-3')}>
                {options.map((o) => {
                    const on = line.grade === o.grade;
                    return (
                        <button
                            key={o.grade}
                            type="button"
                            role="radio"
                            aria-checked={on}
                            onClick={() => choose(o.grade)}
                            className={cn(
                                'min-h-11 rounded-lg border px-2 py-2 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                                on ? SELECTED[o.grade] : 'bg-background hover:bg-accent',
                            )}
                        >
                            {o.label}
                        </button>
                    );
                })}
            </div>
            {unanswered && <p className="mt-2 text-sm text-red-700 dark:text-red-400">Answer this line.</p>}

            {flagged && (
                <div className="mt-3 space-y-3 border-t pt-3">
                    {!open && line.grade === 'fix_now' && (
                        <label className="flex items-start justify-between gap-3 rounded-lg bg-muted/60 p-3">
                            <span className="min-w-0">
                                <span className="block text-sm font-medium">Fixed during inspection</span>
                                <span className="block text-xs text-muted-foreground">
                                    Put right now, by the driver or with the driver’s part. Recorded as Fixed with the note; no photo needed.
                                </span>
                            </span>
                            <Switch checked={line.fixedOnSpot} onCheckedChange={(v) => props.onChange({ fixedOnSpot: v })} aria-label="Fixed during inspection" />
                        </label>
                    )}

                    <div className="space-y-2">
                        <label htmlFor={noteId} className="text-sm font-medium">
                            Note {noteNeeded ? <span className="text-red-700 dark:text-red-400">required</span> : <span className="font-normal text-muted-foreground">optional</span>}
                        </label>
                        <Textarea
                            id={noteId}
                            rows={2}
                            value={line.note}
                            onChange={(e) => props.onChange({ note: e.target.value })}
                            placeholder={line.grade === 'not_needed' ? 'Why no fix is needed' : 'What is wrong with the car'}
                            className={cn(showProblems && problems.has('note') && 'border-red-500')}
                        />
                        {line.grade !== 'not_needed' && line.grade !== 'fixed' && (
                            <div className="flex flex-wrap gap-1.5">
                                {NOTE_CHIPS.map((chip) => (
                                    <button
                                        key={chip}
                                        type="button"
                                        onClick={() => addChip(chip)}
                                        className="min-h-10 rounded-full border bg-background px-3 text-sm hover:bg-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                    >
                                        {chip}
                                    </button>
                                ))}
                            </div>
                        )}
                        {showProblems && problems.has('note') && <p className="text-sm text-red-700 dark:text-red-400">A note is needed.</p>}
                    </div>

                    <div className="space-y-2">
                        {(line.photos.length > 0 || pending.length > 0) && (
                            <ul className="flex flex-wrap gap-3 pt-1">
                                {line.photos.map((url) => (
                                    <li key={url} className="relative">
                                        <PhotoThumb url={url} previewUrl={previews[url]} alt={`${label} photo`} className="h-16 w-16" />
                                        <button
                                            type="button"
                                            onClick={() => props.onRemovePhoto(url)}
                                            className="absolute -right-2 -top-2 flex h-9 w-9 items-center justify-center rounded-full border bg-background shadow-sm after:absolute after:-inset-1 after:content-['']"
                                            aria-label="Remove this photo"
                                        >
                                            <X className="h-3.5 w-3.5" aria-hidden />
                                        </button>
                                    </li>
                                ))}
                                {pending
                                    .filter((p) => p.status !== 'done')
                                    .map((p) => (
                                        <li key={p.name} className="flex h-16 w-16 flex-col items-center justify-center gap-1 overflow-hidden rounded-md border bg-muted text-center">
                                            {p.status === 'preparing' || p.status === 'uploading' ? (
                                                <Loader2 className="h-5 w-5 animate-spin" aria-label="Uploading" />
                                            ) : p.status === 'error' ? (
                                                <button type="button" onClick={() => props.onRetry(p.name)} className="flex flex-col items-center text-xs text-red-700 dark:text-red-400">
                                                    <RotateCw className="h-4 w-4" aria-hidden />
                                                    Retry
                                                </button>
                                            ) : (
                                                <button type="button" onClick={() => props.onDismiss(p.name)} className="px-1 text-[11px] leading-tight text-red-700 dark:text-red-400">
                                                    Unreadable · dismiss
                                                </button>
                                            )}
                                        </li>
                                    ))}
                            </ul>
                        )}
                        {pending.some((p) => p.status === 'unreadable') && (
                            <p className="text-xs text-red-700 dark:text-red-400">{pending.find((p) => p.status === 'unreadable')?.error}</p>
                        )}
                        <PhotoInputs
                            compact
                            required={photoRequired && photoCount === 0}
                            cameraLabel={photoRequired && line.photos.length === 0 ? 'Photo required' : 'Add photo'}
                            onFile={props.onPhoto}
                        />
                        {photoRequired && line.photos.length === 0 && (
                            <p className="text-xs text-muted-foreground">
                                A new Must fix needs its own photo before the inspection can be submitted.
                            </p>
                        )}
                        {open && line.grade === 'fixed' && line.photos.length === 0 && (
                            <p className="text-xs text-muted-foreground">A photo of the repair helps if the driver asks later.</p>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
